import classNames from "classnames";
import { ListGroup, ListGroupControl, ListGroupItem, LoadingState, View } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/**
 * EVERY UNIT THIS LEASE HAS OCCUPIED, GROUPED INTO ONE COLLAPSIBLE SECTION PER
 * TERM (GET /v2/amendment-tenancies?lease_agreement_id=:id).
 *
 * Not a stored list: `Amendment::UnitFold` recomputes it from the whole tree on
 * every read, walking the amendments in the order they act — parents before
 * children, earliest first among what is available — and keeping the occupied set
 * as it goes. So it already accounts for every Contraction, Relocation, Renewal
 * hand-back, Termination, correction and cancellation.
 *
 * ---------------------------------------------------------------------------
 * ONE SECTION PER TERM, AND A UNIT CAN BE IN MORE THAN ONE (Sim, 26 Aug)
 * ---------------------------------------------------------------------------
 * A section is a TERM — an amendment that took the current-term pointer, which
 * means an Original Lease or a Renewal. **A Termination gets no section**: it
 * takes the pointer because nothing follows it, not because a term begins, and
 * its units are the ones it ENDED, which belong to the term that was running.
 *
 * A unit occupied across a Renewal arrives TWICE — one row per unit PER TERM,
 * each carrying that term's own dates. HC005 runs from the Expansion's MoveInDate
 * to the day Renewal #10728831 opens under the Original Lease's heading, and from
 * that same day to nothing under the Renewal's. A single interval repeated under
 * both headings was the first shape of this, and it said the Original Lease's
 * term never ended.
 *
 * The server decides all of it (`term_pointer` on each row) — this file only
 * groups by it. Sections run oldest first, so the history reads downward.
 *
 * EVERY SECTION IS CLOSED BY DEFAULT (Sim, 26 Aug). The header carries a STATUS
 * TAG (Sim, 4 Sep) so a closed term still says whether it is the one running —
 * see `sectionStatus`.
 *
 * ---------------------------------------------------------------------------
 * THE COLUMNS
 * ---------------------------------------------------------------------------
 *   Status      one word, from the server (`status`) — `current`, `future` or
 *               `ended`. Not re-derived here, so the tag and the sort can never
 *               disagree about what a row is.
 *   Unit Area   the tree's answer, not one amendment's. The LAST node to state an
 *               area for the unit wins whatever its dates, so a Remeasure applies
 *               immediately and there is nothing to revert.
 *   Start Date  the day the unit was OCCUPIED. Which column of the payload dates
 *               that depends on the amendment type — MoveInDate for an Original
 *               Lease, an Expansion and a Relocation's "In", AmendmentStartDate
 *               for a Renewal — and the rule lives in Amendment::TypeBehaviour.
 *   End Date    usually "—". A unit only goes vacant when an amendment TAKES IT
 *               AWAY: a Renewal that drops it, a Contraction, a Relocation's
 *               "Out", a Termination. A term merely EXPIRING vacates nothing.
 *
 * Introduced By and Project stay beside them. "Which amendment put this unit
 * here" is NOT always the amendment whose window covers today: a Renewal carrying
 * a unit forward does not restart the tenant's occupancy, so the unit keeps the
 * Original Lease's MoveInDate and the Original Lease stays the introducer.
 *
 * SORT AND SEARCH ARE CLIENT-SIDE and apply WITHIN each section, like the
 * Amendments tab and for the same reason: the whole set arrives in one request.
 * **SEARCHING OPENS EVERY SECTION THAT MATCHES** (Sim, 26 Aug) — otherwise a hit
 * inside a collapsed term is invisible, and the header carries no count to hint
 * at one (Sim took the dates and the count off it; the header is the term's NAME
 * and nothing else). While a search is running it therefore WINS over a manual
 * toggle: you cannot collapse a section that is answering your search.
 */

interface Props {
	agreement: tcpinpoint.LeaseAgreement;
	name?: string;
	icon?: string;
}

const dash = (value: any) => (value === 0 || !!value ? value : "—");

// Same format as the lease's Details tab, so the term on the page header and the
// date on a row are the same string.
const formatDate = (value?: string | null) => (!!value ? moment(value).format("D MMM YYYY") : null);

/**
 * The status tag. `badge-*` variants rather than colours of our own: the same
 * classes the Amendments tab's Cancelled and Branch badges use, and only the
 * current term's units get the loud one — the rest are history.
 */
const STATUS_BADGES: Record<string, { label: string; className?: string }> = {
	current: { label: "tenancy_status_current", className: "badge-primary" },
	future: { label: "tenancy_status_future" },
	ended: { label: "tenancy_status_ended" }
};

const StatusTag = ({ status }: { status?: string | null }) => {
	const badge = STATUS_BADGES[_.toString(status)];
	if (!badge) return <React.Fragment>{dash(null)}</React.Fragment>;

	return <span className={classNames("badge", badge.className)}>{translate(badge.label)}</span>;
};

/**
 * THE TAG ON A SECTION HEADER (Sim, 4 Sep) — the same badge the rows carry, so a
 * COLLAPSED term still says whether it is the one running.
 *
 * Derived from the rows rather than asked of the server: `term_pointer` carries
 * only the node's ids and type, and the fold has already decided each row's
 * status — re-deriving the term's from dates here is how the header and the rows
 * end up disagreeing.
 *
 * A term is whatever its LOUDEST row is: `current` if any unit is occupied under
 * it now, else `future` if any is still to start, else `ended`. Occupied wins
 * because a Renewal that has already taken one unit early is still the running
 * term, and a term nobody has reached is future even though its rows sit above
 * an ended one.
 *
 * ALL ROWS, NOT THE FILTERED ONES: a search narrows what is listed, never what
 * the term IS.
 */
const sectionStatus = (rows: any[]) => {
	const statuses = _.map(rows, row => _.toString(_.get(row, "status")));
	if (_.includes(statuses, "current")) return "current";
	if (_.includes(statuses, "future")) return "future";
	if (_.includes(statuses, "ended")) return "ended";
	return null;
};

/**
 * An amendment as a link to its own page, e.g. a muted "#10728826" that opens
 * `/lease-agreements/:id/amendments/:amendmentId`.
 *
 * MIND THE TWO FAMILIES OF ID: the ROUTE takes the amendment's UUID, the LABEL
 * is Yardi's AmendmentID string. Never route on the one that is shown.
 *
 * A NEW TAB, never this one (Sim, 25 Aug). The list is what the reader is working
 * through — sending them away from a row they scrolled to find loses their place.
 * `rel` because a plain target="_blank" hands the opened page a live
 * `window.opener`.
 */
const amendmentLink = (leaseAgreementId: string, id?: string | null, yardiId?: string | null) => {
	if (!yardiId) return null;
	if (!id) return <span className="muted">#{yardiId}</span>;
	return (
		<Link to={`/lease-agreements/${leaseAgreementId}/amendments/${id}`} target="_blank" rel="noopener noreferrer">
			#{yardiId}
		</Link>
	);
};

/**
 * THE END DATE OF A SEEDED TERM IS THE LEASE'S OWN END (Sim, 31 Aug).
 *
 * A seeded node never VACATES anything — the AFG migration extract states a
 * lease's window, not a movement — so Amendment::UnitFold leaves `end_date` nil
 * and this column read "—" for every seeded lease, even though the extract's
 * LeaseTo column says exactly when the lease runs out. That date is on the
 * payload already as the introducer's `amendment_end_date`.
 *
 * SCOPED TO THE SEED TYPES, deliberately. For a Yardi amendment
 * `amendment_end_date` is the PAPERWORK window, and showing it here would
 * resurrect the rule Sim removed on 26 Aug: a unit silently going vacant the day
 * its node's end date passed. A seed is the one node whose window IS the lease.
 *
 * A blank LeaseTo still reads "—" — dateCell's own answer to a missing date.
 */
const SEED_AMENDMENT_TYPES = ["seed original lease", "seed expansion"];

const seededTerm = (unit: any) =>
	_.includes(SEED_AMENDMENT_TYPES, _.toLower(_.toString(_.get(unit, "amendment_type"))));

const termEndDate = (unit: any) =>
	_.get(unit, "end_date") || (seededTerm(unit) ? _.get(unit, "amendment_end_date") : null);

/**
 * WHICH NODE TO CREDIT UNDER THE END DATE.
 *
 * Normally `term_amendments[1]`, the node that VACATED the unit. A seeded term
 * has no such node — the date is the seed's OWN window — so the credit goes to
 * [0], the seed that introduced the unit and states the term. Without this the
 * End Date sat there with nothing under it while the Start Date beside it named
 * the same amendment, which reads as a missing link rather than a deliberate one.
 */
const termEndAmendment = (unit: any, terms: any[]) =>
	_.get(unit, "end_date") ? _.get(terms, 1) : _.get(terms, 0);

/**
 * A date cell: the date, and under it the amendment that set it.
 *
 * `term_amendments` arrives in the order the nodes act — [0] occupied the unit,
 * [1] vacated it — so each column takes its own index rather than deciding for
 * itself which node belongs to which date.
 */
const dateCell = (leaseAgreementId: string, date: any, amendment: any) => {
	const formatted = formatDate(date);
	if (!formatted) return <React.Fragment>{dash(null)}</React.Fragment>;

	return (
		<React.Fragment>
			<div style={{ whiteSpace: "nowrap" }}>{formatted}</div>
			<div>{amendmentLink(leaseAgreementId, _.get(amendment, "id"), _.get(amendment, "yardi_id"))}</div>
		</React.Fragment>
	);
};

// Chevrons, matching SlaMonitor's CollapsibleTaskSection — the app has no shared
// collapsible in components/Shared, and inline SVG beats depending on whichever
// FontAwesome set happens to be loaded.
const Chevron = ({ open }: { open: boolean }) => (
	<svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
		<path
			d={open ? "M2 4L6 8L10 4" : "M4 2L8 6L4 10"}
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
		/>
	</svg>
);

// Both are KEYS ON THE SERIALIZED ROW, because ListGroupControl uses each one as
// the translate() label as well — a sort option that is not a dictionary key
// renders as a purple debug span.
const SORT_OPTIONS = ["unit_code", "occupied"];

/**
 * Occupied above everything else, then alphabetical inside each group. The first
 * iteratee collapses to a constant when sorting by code, so the two sorts are one
 * expression and the tie-break is the same either way.
 */
const sortIteratees = (sortBy: string) => [
	(unit: any) => (sortBy === "occupied" ? (!!_.get(unit, "occupied") ? 0 : 1) : 0),
	(unit: any) => _.toLower(_.toString(_.get(unit, "unit_code")))
];

const TenanciesTab = ({ agreement }: Props) => {
	// null = no response yet. Driven from our own state rather than
	// `datasource.isLoading`: with no `renderTrigger` the datasource cannot
	// re-render this component when it flips isLoading back to false.
	const [units, setUnits] = useState<any[] | null>(null);
	const [sortBy, setSortBy] = useState("occupied");
	const [searchTerm, setSearchTerm] = useState("");
	// Which sections the reader has opened, keyed on the pointer's uuid. Empty to
	// begin with — every section starts CLOSED (Sim, 26 Aug).
	const [toggled, setToggled] = useState<Record<string, boolean>>({});
	const [datasource] = useState(new Datasource({ mainModelName: "amendment_tenancy" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setUnits(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (_.isEmpty(agreement.id)) return;
		datasource.get("v2", "amendment-tenancies", {
			queryStringParameters: { lease_agreement_id: agreement.id, per_page: 1000 }
		});
	}, [datasource, agreement.id]);

	/**
	 * THE SECTIONS, oldest first. Built from the rows rather than from a second
	 * request: every row names the term it belongs to, so the set of sections is
	 * whatever those mention.
	 *
	 * ORDER IS FIRST APPEARANCE, and the server hands the rows over grouped by term
	 * oldest-first — so a unit that exists only in the newest term cannot pull that
	 * term up the page.
	 */
	const sections = useMemo(() => {
		const order: string[] = [];
		const byId: Record<string, { pointer: any; rows: any[] }> = {};

		_.forEach(units || [], unit => {
			const pointer = _.get(unit, "term_pointer");
			const id = _.toString(_.get(pointer, "id"));
			if (!id) return;
			if (!byId[id]) {
				byId[id] = { pointer, rows: [] };
				order.push(id);
			}
			byId[id].rows.push(unit);
		});

		return _.map(order, id => byId[id]);
	}, [units]);

	if (units === null) return <LoadingState />;

	// Everything a row SHOWS is searchable — the unit code, the tenancy name and
	// every amendment on the row, so "which amendment brings HC005 in" and "what
	// is #10728832 doing here" are both answerable from the box.
	const search = searchTerm.trim().toLowerCase();
	const matches = (unit: any) => {
		if (!search) return true;
		return _.some(
			[
				_.get(unit, "unit_code"),
				_.get(unit, "tenancy_name"),
				_.get(unit, "amendment_type"),
				_.get(unit, "amendment_yardi_id"),
				..._.flatMap(_.castArray(_.get(unit, "term_amendments") || []), amendment => [
					_.get(amendment, "type"),
					_.get(amendment, "yardi_id")
				])
			],
			field => _.toLower(_.toString(field)).includes(search)
		);
	};

	const renderRow = (unit: any) => (
		<ListGroupItem
			id={_.get(unit, "id")}
			key={_.get(unit, "id")}
			heading={dash(_.get(unit, "unit_code"))}
			subheading={_.get(unit, "tenancy_name") || undefined}
			datapoints={[
				// STATUS FIRST, between the unit's name and its numbers (Sim, 26 Aug).
				// The datapoint key is `tenancy_status`, NOT `status`: the key doubles
				// as a CSS class and the global `.status` rule triples the font size.
				{ key: "tenancy_status", value: <StatusTag status={_.get(unit, "status")} /> },
				{ key: "unit_area", value: <React.Fragment>{dash(_.get(unit, "unit_area"))}</React.Fragment> },
				{
					key: "start_date",
					value: dateCell(
						agreement.id,
						_.get(unit, "start_date"),
						_.get(_.castArray(_.get(unit, "term_amendments") || []), 0)
					)
				},
				{
					key: "end_date",
					value: dateCell(
						agreement.id,
						termEndDate(unit),
						termEndAmendment(unit, _.castArray(_.get(unit, "term_amendments") || []))
					)
				},
				// WHICH AMENDMENT PUT THIS UNIT ON THE LEASE — the whole reason this is
				// not just a list of unit codes.
				{
					key: "amendment_introduced_by",
					value: (
						<React.Fragment>
							{_.get(unit, "amendment_type") || translate("unknown")}{" "}
							{amendmentLink(agreement.id, _.get(unit, "amendment_id"), _.get(unit, "amendment_yardi_id"))}
						</React.Fragment>
					)
				},
				{
					key: "project",
					value: (
						<React.Fragment>
							{!!_.get(unit, "project_id") ? (
								<Link
									to={`/projects/${_.get(unit, "project_id")}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									{translate("amendment_open_project")}
								</Link>
							) : (
								dash(null)
							)}
						</React.Fragment>
					)
				}
			]}
			className={classNames({ "text-danger": !!_.get(unit, "amendment_cancelled") })}
		/>
	);

	const areaFigure = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 2 });

	return (
		<View>
			{/* Kept, unlike the rest of the summary: the current-term pointer sits on a
			    CANCELLED amendment, so the term the lease is running to is one nobody
			    honours. Only renders when that is actually the case. */}
			{!!agreement.cancelled_amendment && (
				<div className="alert alert-warning" role="alert">
					{translate("lease_agreement_cancelled_term_warning")}
				</div>
			)}

			<ListGroupControl
				onSortChange={setSortBy}
				selectedSort={sortBy}
				sortOptions={SORT_OPTIONS}
				onSearchChange={setSearchTerm}
				searchValue={searchTerm}
				searchLabel="search_units"
			/>

			{/* No sections at all means the tree named no units — a real answer (a
			    Termination's subtree introduces nothing), so it gets the same shared
			    empty state an empty list would. */}
			{_.isEmpty(sections) ? (
				<ListGroup resource="tenancies" collection={[]} pagination={false} renderRow={renderRow} />
			) : (
				_.map(sections, section => {
					const pointerId = _.toString(_.get(section, "pointer.id"));
					const rows = _.filter(_.sortBy(section.rows, sortIteratees(sortBy)), matches);
					// CLOSED unless the reader opened it — or unless a search is running
					// and this section answers it, which WINS over the toggle. Without
					// that a match inside a collapsed term is invisible: the header
					// carries no count to hint at one.
					const open = !!search ? rows.length > 0 : !!toggled[pointerId];
					const areaTotal = _.sumBy(rows, (unit: any) => Number(_.get(unit, "unit_area")) || 0);

					return (
						<div key={pointerId} className="lease-agreement-term">
							{/* A plain <button> rather than an <a href="#">: this toggles, it
							    does not navigate, and a hash href would fight `Tabs`, which
							    reads the URL hash to decide which tab is showing. */}
							<button
								type="button"
								className="lease-agreement-term-header"
								onClick={() => setToggled(previous => ({ ...previous, [pointerId]: !open }))}
								aria-expanded={open}
							>
								<span className="lease-agreement-term-chevron">
									<Chevron open={open} />
								</span>
								<span className="lease-agreement-term-title">
									{_.get(section, "pointer.type") || translate("amendment")}{" "}
									{amendmentLink(agreement.id, pointerId, _.get(section, "pointer.yardi_id"))}
									{/* Nothing rather than StatusTag's "—" dash: a header with no
									    status is a term whose rows the fold said nothing about, and
									    a dash beside the term's name reads as a missing value. */}
									{!!sectionStatus(section.rows) && <StatusTag status={sectionStatus(section.rows)} />}
								</span>
								<span className="lease-agreement-term-area">
									<span className="lease-agreement-term-area-value">{areaFigure(areaTotal)}</span>
									<span className="lease-agreement-term-area-meta">
										{rows.length} {translate(rows.length === 1 ? "unit" : "units")}
									</span>
								</span>
							</button>

							{open && (
								<ListGroup
									resource="tenancies"
									collection={rows}
									pagination={false}
									renderRow={renderRow}
								/>
							)}
						</div>
					);
				})
			)}
		</View>
	);
};

export default memo(TenanciesTab);
